var _ = require('lodash'),
  configManager = require('../config/configManager'),
  RSVP = require('rsvp'),
  perf = require('./performanceMetrics'),
  log = require('../libraries/logger').getLoggerInstance('Base.cors'),
  cache = require('../models/cache');

var privateMethods;

/**
 * Constructs a new CorsClient
 * @param {string} baseUrl - base url for the cors client
 * @param {boolean} isAsync - async flag
 * @param {number} timeout - timeout in milliseconds
 * @param {boolean} [retry=true] - Should the request be retried if it fails. When set to false, this overrides the merchants configurations to retry a request
 * @param {number} maxRequestRetries - maximum number of retries
 * @param {function} endpointMonitorCallback - the endpoint monitor callback function
 * @returns {object} a new CorsClient instance
 */
function CorsClient(baseUrl, isAsync, timeout, retry, maxRequestRetries, endpointMonitorCallback) {
  this.baseUrl = baseUrl;
  this.timeout = configManager.getSystemConfig().baseRequestTimeout

  this.isAsync = (isAsync == undefined ? true : isAsync); // eslint-disable-line eqeqeq
  this.retryEnabled = (retry == undefined ? true : retry); // eslint-disable-line eqeqeq
  this.maxRetries = (maxRequestRetries == undefined ? configManager.getMerchantConfig().maxRequestRetries : maxRequestRetries); // eslint-disable-line eqeqeq
  this.retryCount = 0;
  this.endpointMonitorCallback = endpointMonitorCallback;

  if (isNaN(this.maxRetries) || this.maxRetries < 0) {
    this.maxRetries = configManager.getMerchantDefaultConfig().maxRequestRetries;
  }

  // Look for new ExtendedTimeout merchant configuration option
  var extendedTimeout = timeout == undefined ? configManager.getMerchantConfig().extendedTimeout : timeout // eslint-disable-line eqeqeq  
  if (isNaN(extendedTimeout) || extendedTimeout < 0) {
    // If we don't find a valid value, look for a legacy timeout value
    extendedTimeout = configManager.getMerchantConfig().timeout
  }

  if (!isNaN(extendedTimeout) && extendedTimeout > 0) {
    if (extendedTimeout <= 3999) {
      extendedTimeout = 4000;
    }
    this.extendedTimeout = extendedTimeout
    this.retryEnabled = retry !== false ? true : false

    /**
     * INSIGHT - If we have an extendedTimeout but no maxRetries value we need to default maxRetries to 1 to make sure we retry at least once. Default value is 0
     * since retrying requests is an opt in configuration (as of 10/2019 ExtendedTimeout.feature). This helps ensure backwards compatibility with the old Timeout config
     */
    if (isNaN(this.maxRetries) || this.maxRetries < 1) {
      var merchantDefaultRetries = configManager.getMerchantDefaultConfig().maxRequestRetries
      // INSIGHT - To make this feature easier to use
      if (isNaN(merchantDefaultRetries) || merchantDefaultRetries < 1) {
        this.maxRetries = 1;
      }
    }
  } else {
    this.retryEnabled = false;
    log.debug("Disabling retry logic due to invalid extended timeout value: " + extendedTimeout);
  }

  return privateMethods.createXhrObject(this);
}

privateMethods = {
  createXhrObject: function (context) {
    context.xhr = new XMLHttpRequest();

    // Look to see if we need to change the xhr object for IE8 + IE9 support
    if (typeof XDomainRequest != 'undefined' && !('withCredentials' in context.xhr)) {
      context.xhr = new XDomainRequest();

      // Check to make sure our xhr obj supports cors
    } else if (!('withCredentials' in context.xhr)) {
      // NEXTREV: review and decide if we need to do something with this like
      // throw a fatal

      // Cors not supported by browser
      log.error('Cors not supported by this browser');
      return false;
    }
  },
  sendRequest: function (method, relativePath, data, isRetry) {
    var context = this,
      retryObject = arguments,
      fullPath = context.baseUrl + relativePath;

    context.relativePath = relativePath;
    context.method = method;
    context.fullPath = fullPath;

    // INSIGHT - On retries we want to re-use the same deferred object since it gets returned back to the process waiting for http request result
    if (!isRetry) {
      context.deferred = RSVP.defer();
    }

    // INSIGHT - Store the arguments in memory in the event we need to retry the request.
    context.retryObject = Array.prototype.slice.call(retryObject);
    if (context.xhr) {
      // NEXTREV: This object can only handle 1 connection at a time.
      // Previous attempts did not work in IE8-9. Consider
      // a compatibile solution for IE as well..

      context.xhr.open(method, fullPath, context.isAsync);

      context.xhr.onerror = function (error) {
        log.debug('XHR Error event triggered');
        privateMethods.handleError(context, error, 'error');
      };
      context.xhr.ontimeout = function (error) {
        log.debug('Timeout error occurred');
        privateMethods.handleError(context, error, 'timeout');
      };
      context.xhr.onload = function () {
        privateMethods.handleResponse(context);
      };

      // INSIGHT - IE requires that timeout be set between open and send functions. Additionally sync requests cannot have a timeout value
      if (context.isAsync) {
        context.xhr.timeout = context.timeout;
      }

      log.info('Sending ' + (context.isAsync ? 'async ' : 'sync ') + method + ' request to ' + fullPath + ' with a timeout value of ' + context.timeout + 'ms');

      perf.mark('request_' + relativePath);
      if ('setRequestHeader' in context.xhr) {
        context.xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
        context.xhr.setRequestHeader('X-Cardinal-Tid', 'Tid-' + cache.tid);
      }

      context.xhr.send(JSON.stringify(data));

      // INSIGHT - Setup a safety net timeout for browsers that don't support timeouts or are currently having issues with them (2016 iOS, old browsers, etc). We will add a few millis to the timeout to allow for browsers that support the timeout to properly handle it.
      context.timeoutMonitor = setTimeout(function () {
        // Only abort if the request has not finished
        if (context.xhr.readyState !== 4) {
          context.xhr.abort();
          privateMethods.handleError(context, 'Timeout Occurred');
        }
      }, context.timeout + 10);

    } else {
      context.deferred.reject('Invalid xhr object [%o]', context.xhr);
    }

    return context.deferred.promise;
  },
  // NOTE - A status = 0 for a post can indicate an issue with the OPTIONS call. The browser does not provide any additional information to help diagnose it, but the browser may log to its own console the reason for the failure.
  handleError: function (context, error, type) {
    clearTimeout(context.timeoutMonitor);

    if (context.retryCount < context.maxRetries) {
      privateMethods.attemptRetry(context);
    } else {
      log.debug("Error connecting to Service");
      privateMethods.logRequestPreformanceData('Request Failed - ' + type, null, false, context);
      log.debug(error);

      context.deferred.reject("Connectivity Error");
    }
  },
  handleResponse: function (context) {
    try {
      var contentType, respData;

      clearTimeout(context.timeoutMonitor);

      if ('getResponseHeader' in context.xhr) {
        // Sane browsers
        contentType = context.xhr.getResponseHeader('Content-Type');
      } else {
        //IE8-9
        contentType = context.xhr.contentType;
      }

      log.debug('Content-Type: ' + contentType);

      respData = privateMethods.parseResponseAsJson(context.xhr.responseText);

      if (context.xhr.status !== undefined) {
        if (context.xhr.status === 200 || context.xhr.status === 304) {
          log.info("CORS message completed successfully");
          privateMethods.logRequestPreformanceData('CORS Connection Results', respData, true, context);
          context.deferred.resolve(respData);

        } else if (context.xhr.status >= 500 && context.xhr.status < 600) {

          privateMethods.logRequestPreformanceData('Failed Request, retrying', respData, false, context);
          privateMethods.attemptRetry(context);

        } else {
          log.info("CORS message completed with issues");
          privateMethods.logRequestPreformanceData('CORS Connection Issue Encountered', respData, true, context);

          privateMethods.attemptRetry(context);
        }
      } else {
        // IE8-9
        log.info('CORS message completed');
        context.deferred.resolve(respData);
        privateMethods.logRequestPreformanceData('CORS Connection Results (IE)', respData, true, context);
      }

    } catch (error) {
      log.error("Exception thrown while parsing response: " + error);
      context.deferred.reject(error);
    }

  },
  /**
   * Helper function to parse a response into JSON
   * @param {string} responseText - The text to turn into JSON
   * @returns {Object} the object form of the responseText. An empty object is returned if parsing failed.
   */
  parseResponseAsJson: function (responseText) {
    var response;
    if (responseText.length > 0) {
      try {
        response = JSON.parse(responseText);
      } catch (e) {
        log.error("Failed to parse response as JSON", e);
        response = {};
      }
    } else {
      response = {};
    }

    return response;
  },
  /**
   * Helper method to gather elapsed time statistics consistently
   * @param {string} relativePath - The path section of the URL
   * @param {string} method - The HTTP method used on the request
   * @return {int} elasped time in milliseconds
   */
  getTimeStatistics: function (relativePath, method) {
    var measureKey = method + '_' + relativePath;

    perf.mark('response_' + relativePath);
    perf.measure(measureKey, 'request_' + relativePath, 'response_' + relativePath);

    return perf.getElapsed(measureKey);
  },
  /**
   * Log data about the request for metrix tracking
   * @param {string} message - A message describing the request result
   * @param {string=} responseData - The string result of the response, if there was one
   * @param {boolean} wasRequestSuccessful - Flag to indicate the HTTP request is considered successful. This flag does not take into account an error message within the response data, simply the transportation was successful
   * @param {object} context - The instance context object
   */
  logRequestPreformanceData: function (message, responseData, wasRequestSuccessful, context) {
    // Due to possible polyfill needed for performance scripts we need to async handle getting elapsed time
    log.debug({
      message: message,
      wasRequestSuccessful: wasRequestSuccessful,
      statusCode: context.xhr.status,
      domain: context.baseUrl,
      path: context.relativePath,
      method: context.method,
      elapsedTime: privateMethods.getTimeStatistics(context.relativePath, context.method),
      isAsync: context.isAsync,
      timeout: context.xhr.timeout,
      response: responseData,
      maxRetries: context.maxRetries,
      currentRetryIteration: context.retryCount,
      testGroup: context.testGroup
    });

    if (context.endpointMonitorCallback) {
      context.endpointMonitorCallback(context.fullPath, wasRequestSuccessful);
    }
  },
  attemptRetry: function (context) {
    if (context.retryEnabled && context.retryCount < context.maxRetries) {
      var httpMethod = context.retryObject.shift();
      log.info('connection failed, retrying request');

      context.retryCount++;
      privateMethods.createXhrObject(context);
      // Set isRetry flag to true
      context.retryObject.push(true);
      context.timeout = context.extendedTimeout

      context[httpMethod].apply(context, context.retryObject);
    } else {
      log.info("Request retries are disabled or max retry attempts has been reached, connection failed");
      context.deferred.reject({})
    }
  }
};


CorsClient.prototype = {
  get: _.partial(privateMethods.sendRequest, 'get'),
  post: _.partial(privateMethods.sendRequest, 'post')
};

module.exports = CorsClient;


//////////////////
// WEBPACK FOOTER
// ./libraries/cors.js
// module id = 65
// module chunks = 0 5 20